<template>
  <div class="min-h-screen bg-[#fafbfc] px-4 py-10 font-['Poppins'] sm:px-6 lg:px-8">
    <div class="mx-auto w-full max-w-md rounded-xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-8">
      <img
        class="mx-auto h-8 w-auto"
        src="@/assets/media/images/platforma-logo-in-color.svg"
        alt="Platforma Logo"
      >
      <h1 class="mt-6 text-center text-2xl font-semibold text-zinc-900">
        Choose A New Password
      </h1>

      <p
        v-if="isVerifying"
        class="mt-4 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-600"
      >
        Verifying reset link...
      </p>

      <p
        v-if="verifyError"
        class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600"
      >
        {{ verifyError }}
      </p>

      <form
        v-if="isTokenValid && !resetSuccess"
        class="mt-6 space-y-4"
        @submit.prevent="submitPasswordReset"
      >
        <div>
          <label
            for="new-password"
            class="block text-sm font-medium text-zinc-700"
          >New password</label>
          <input
            id="new-password"
            v-model="newPassword"
            type="password"
            autocomplete="new-password"
            required
            minlength="8"
            class="mt-1 block w-full rounded-md border-0 py-2 text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm"
            :disabled="isSubmitting"
          >
        </div>

        <div>
          <label
            for="confirm-password"
            class="block text-sm font-medium text-zinc-700"
          >Confirm password</label>
          <input
            id="confirm-password"
            v-model="confirmPassword"
            type="password"
            autocomplete="new-password"
            required
            minlength="8"
            class="mt-1 block w-full rounded-md border-0 py-2 text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm"
            :disabled="isSubmitting"
          >
        </div>

        <button
          type="submit"
          class="inline-flex w-full items-center justify-center rounded-md bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#2577d9] disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isSubmitting || !newPassword || !confirmPassword"
        >
          {{ isSubmitting ? 'Resetting...' : 'Reset Password' }}
        </button>
      </form>

      <p
        v-if="resetError"
        class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600"
      >
        {{ resetError }}
      </p>

      <p
        v-if="resetSuccess"
        class="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700"
      >
        {{ resetSuccess }}
      </p>

      <div class="mt-6 text-center">
        <RouterLink
          to="/"
          class="text-sm font-medium text-[#2D8CFF] hover:underline"
        >
          Back to login
        </RouterLink>
        <span class="mx-2 text-zinc-300">|</span>
        <RouterLink
          to="/password-recover"
          class="text-sm font-medium text-[#2D8CFF] hover:underline"
        >
          Request new reset link
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

const isDev = process.env.NODE_ENV === 'development';
const baseUrl = isDev
  ? process.env.VUE_APP_API_PROXY_URL
  : '';

const route = useRoute();

const token = ref('');
const isVerifying = ref(true);
const isTokenValid = ref(false);
const verifyError = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const isSubmitting = ref(false);
const resetError = ref('');
const resetSuccess = ref('');

const verifyToken = async () => {
  const rawToken = String(route.query?.resetToken || route.query?.token || '').trim();
  token.value = rawToken;
  if (!rawToken) {
    isVerifying.value = false;
    verifyError.value = 'Missing password reset token.';
    return;
  }

  try {
    const response = await fetch(
      `${baseUrl}/api/auth/password-recovery/verify?token=${encodeURIComponent(rawToken)}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
      throw new Error(String(payload?.message || payload?.error || 'Invalid password reset link.'));
    }
    isTokenValid.value = true;
  } catch (error) {
    console.error('Error verifying password reset token:', error);
    verifyError.value = String(error?.message || 'Unable to verify password reset link.');
    isTokenValid.value = false;
  } finally {
    isVerifying.value = false;
  }
};

const submitPasswordReset = async () => {
  resetError.value = '';
  resetSuccess.value = '';

  if (!token.value) {
    resetError.value = 'Missing password reset token.';
    return;
  }
  if (!newPassword.value || !confirmPassword.value) {
    resetError.value = 'Please enter and confirm your new password.';
    return;
  }
  if (newPassword.value !== confirmPassword.value) {
    resetError.value = 'New password and confirmation must match.';
    return;
  }
  if (newPassword.value.length < 8) {
    resetError.value = 'Password must be at least 8 characters.';
    return;
  }

  isSubmitting.value = true;
  try {
    const response = await fetch(`${baseUrl}/api/auth/password-recovery/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        token: token.value,
        newPassword: newPassword.value,
        confirmPassword: confirmPassword.value,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
      throw new Error(String(payload?.message || payload?.error || 'Failed to reset password.'));
    }
    resetSuccess.value = String(payload?.message || 'Password reset successfully. You can sign in now.');
    newPassword.value = '';
    confirmPassword.value = '';
  } catch (error) {
    console.error('Error resetting password:', error);
    resetError.value = String(error?.message || 'Unable to reset password right now.');
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  verifyToken();
});
</script>
